/**
 * Regicide score bar — collapsible. Default-collapsed on small screens
 * so the board has the vertical real estate; user can pop it open from
 * the small pill in the top-right.
 *
 * Matches Swoop's collapse pattern (same `.swoop-score-collapsed` /
 * `.swoop-score-collapse-btn` CSS classes for visual consistency).
 *
 *   - Current trump suit + rank direction (the two things that can change
 *     trick-to-trick).
 *   - Per-player race-to-target bars (default 50).
 *   - Tricks taken this round per player.
 *
 * Reuses .score-bar* and .euchre-race-* CSS classes for the bars.
 */

import { useState } from "react";
import type { RegicidePublicState } from "../../../../shared/games/regicide.js";
import { useWindowWidth } from "../../lib/uiHooks.js";

const PHASE_LABEL: Record<string, string> = {
  pickup_choice: "Pickup",
  discard: "Discard",
  dealer_choose_trump: "Choose trump",
  play: "",
  after_trick: "Set trump for next trick",
  round_end: "Round over",
  match_end: "Match over",
};

const SEAT_COLORS = [
  "#5ec8e2", // cyan
  "#f08b5b", // orange
  "#9d6bd6", // purple
  "#e3c95b", // gold
];

export function RegicideScoreBar({
  state,
  yourSeat,
}: {
  state: RegicidePublicState;
  yourSeat: number | null;
}) {
  // Default collapsed on small viewports (same break-point as Swoop).
  const windowWidth = useWindowWidth();
  const [open, setOpen] = useState(() => windowWidth > 700);

  function colorFor(seat: number): string {
    if (yourSeat !== null && seat === yourSeat) return "var(--my-team)";
    return SEAT_COLORS[seat % SEAT_COLORS.length]!;
  }

  if (!open) {
    // Collapsed pill — small, unobtrusive, click to expand.
    return (
      <button
        className="swoop-score-collapsed"
        onClick={() => setOpen(true)}
        title="Show scoreboard"
        aria-label="Show scoreboard"
      >
        Round {state.roundNumber} · scores
      </button>
    );
  }

  return (
    <div className="score-bar regicide-score-bar" role="img" aria-label="Regicide score">
      <div className="score-bar-header">
        <div className="score-bar-side defenders">
          <span className="role-label">
            ROUND {state.roundNumber}
          </span>
          {(PHASE_LABEL[state.phase] ?? state.phase) && (
            <span className="rank-label">
              {PHASE_LABEL[state.phase] ?? state.phase}
            </span>
          )}
        </div>
        <div className="score-bar-center" />
        <div className="score-bar-side attackers">
          <span className="role-label">RACE TO {state.matchTarget}</span>
        </div>
        <button
          className="score-collapse-inline"
          onClick={() => setOpen(false)}
          title="Hide scoreboard"
          aria-label="Hide scoreboard"
        >
          ×
        </button>
      </div>

      <div className="euchre-race-wrap">
        {state.players.map((p) => (
          <div className="euchre-race-row" key={p.seat}>
            <span
              className="euchre-race-label"
              style={{ color: colorFor(p.seat), minWidth: 90, textAlign: "right" }}
              title={p.displayName}
            >
              {p.displayName}
            </span>
            <div className="euchre-race-track">
              <div
                className="euchre-race-fill"
                style={{
                  width: `${Math.min(100, (p.totalScore / state.matchTarget) * 100)}%`,
                  background: colorFor(p.seat),
                }}
              />
              {[10, 20, 30, 40].map((m) => (
                <div
                  key={m}
                  className="euchre-race-tick"
                  style={{ left: `${(m / state.matchTarget) * 100}%` }}
                />
              ))}
              <div className="euchre-race-value">
                {p.totalScore} / {state.matchTarget}
                {p.tricksTaken > 0 && (
                  <span className="muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                    · {p.tricksTaken} trick{p.tricksTaken === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
